import _extends from "@babel/runtime/helpers/extends";
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { forwardRef } from "react";
import Select from "../../elements/select";
import { ValidationMessage } from "../../elements/validation";
import { useDescribedBy } from "../../hooks";

/**
 * @param {string} id Identifier.
 * @param {JSX.Element} error Error node.
 * @param {string} [className] Optional CSS class.
 * @param {boolean} [disabled] Disabled state.
 * @param {string} label Label.
 * @param {JSX.node} [description] Optional description.
 * @param {Object} [props] Any extra props.
 * @returns {JSX.Element} SelectField component.
 */
const SelectField = /*#__PURE__*/forwardRef(({
  id,
  label,
  description,
  disabled,
  validation,
  className,
  ...props
}, ref) => {
  const {
    ids,
    describedBy
  } = useDescribedBy(id, {
    validation: validation === null || validation === void 0 ? void 0 : validation.message,
    description
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classNames("yst-select-field", disabled && "yst-select-field--disabled", className)
  }, /*#__PURE__*/React.createElement(Select, _extends({
    ref: ref,
    id: id,
    label: label,
    labelProps: {
      as: "label",
      className: "yst-label yst-select-field__label"
    },
    disabled: disabled,
    validation: validation,
    className: "yst-select-field__select",
    buttonProps: {
      "aria-describedby": describedBy
    }
  }, props)), (validation === null || validation === void 0 ? void 0 : validation.message) && /*#__PURE__*/React.createElement(ValidationMessage, {
    variant: validation === null || validation === void 0 ? void 0 : validation.variant,
    id: ids.validation,
    className: "yst-select-field__validation"
  }, validation.message), description && /*#__PURE__*/React.createElement("div", {
    id: ids.description,
    className: "yst-select-field__description"
  }, description));
});
SelectField.displayName = "SelectField";
SelectField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.node,
  disabled: PropTypes.bool,
  validation: PropTypes.shape({
    variant: PropTypes.string,
    message: PropTypes.node
  }),
  className: PropTypes.string
};
SelectField.defaultProps = {
  description: null,
  disabled: false,
  validation: {},
  className: ""
};
SelectField.Option = Select.Option;
SelectField.Option.displayName = "SelectField.Option";
export default SelectField;